/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 *
 * Hook for notification mutations (mark as read, delete)
 */

import { createApiClient, isApiFailure } from "@/lib/api/api-client";
import { toastFromApiFailure } from "@/lib/api/toast-utils";
import type { NotificationRow } from "./types";

interface UseNotificationsMutationsOptions {
  auth: {
    getToken: () => Promise<string | null>;
    organizationId: string | null | undefined;
  };
  notifications: NotificationRow[];
  setNotifications: React.Dispatch<React.SetStateAction<NotificationRow[]>>;
  setUnreadCount: React.Dispatch<React.SetStateAction<number>>;
}

/**
 * Hook for notification mutations
 */
export function useNotificationsMutations({
  auth,
  notifications,
  setNotifications,
  setUnreadCount,
}: UseNotificationsMutationsOptions) {
  const markAsRead = async (notificationId: string) => {
    try {
      const api = createApiClient({
        getToken: auth.getToken,
        getOrganizationId: () => auth.organizationId ?? undefined,
        autoRetry429: true,
      });
      const res = await api.patch(`/api/notifications/${notificationId}`);
      if (isApiFailure(res)) {
        if (res.status !== 404) {
          toastFromApiFailure("Failed to mark notification as read", res);
        }
        throw new Error(res.message);
      }

      // Update local state
      setNotifications((prev) =>
        prev.map((notification) =>
          notification.notification_id === notificationId
            ? { ...notification, read_at: new Date().toISOString() }
            : notification,
        ),
      );

      setUnreadCount((prev) => Math.max(0, prev - 1));

      return { success: true };
    } catch (err) {
      return {
        success: false,
        error: err instanceof Error ? err.message : "Unknown error",
      };
    }
  };

  const markAllAsRead = async () => {
    try {
      const api = createApiClient({
        getToken: auth.getToken,
        getOrganizationId: () => auth.organizationId ?? undefined,
        autoRetry429: true,
      });
      const res = await api.post("/api/notifications/mark-all-read", {});
      if (isApiFailure(res)) {
        if (res.status !== 404) {
          toastFromApiFailure("Failed to mark all notifications as read", res);
        }
        throw new Error(res.message);
      }

      // Update local state
      setNotifications((prev) =>
        prev.map((notification) => ({
          ...notification,
          read_at: notification.read_at ?? new Date().toISOString(),
        })),
      );

      setUnreadCount(0);

      return { success: true };
    } catch (err) {
      return {
        success: false,
        error: err instanceof Error ? err.message : "Unknown error",
      };
    }
  };

  const deleteNotification = async (notificationId: string) => {
    try {
      const api = createApiClient({
        getToken: auth.getToken,
        getOrganizationId: () => auth.organizationId ?? undefined,
        autoRetry429: true,
      });
      const res = await api.del<unknown>(
        `/api/notifications/${notificationId}`,
      );
      if (isApiFailure(res)) {
        if (res.status !== 404) {
          toastFromApiFailure("Failed to delete notification", res);
        }
        throw new Error(res.message);
      }

      // Update local state
      const deletedNotification = notifications.find(
        (n) => n.notification_id === notificationId,
      );
      setNotifications((prev) =>
        prev.filter((n) => n.notification_id !== notificationId),
      );

      if (deletedNotification && !deletedNotification.read_at) {
        setUnreadCount((prev) => Math.max(0, prev - 1));
      }

      return { success: true };
    } catch (err) {
      return {
        success: false,
        error: err instanceof Error ? err.message : "Unknown error",
      };
    }
  };

  return {
    markAsRead,
    markAllAsRead,
    deleteNotification,
  };
}
